import type { FileRoutesByFullPath } from '@/routeTree.gen'
import type { SupportedLanguage } from '@/modules/i18n/supportedLanguages'

const SITE_URL = 'https://www.xp-lab.com'

type CanonicalRoutePath = {
  [K in keyof FileRoutesByFullPath]: K extends `/$lang${string}` ? never : K
}[keyof FileRoutesByFullPath]

export interface PrerenderedPage {
  /** Canonical (English) path. Must match a TanStack route in routeTree.gen.ts. */
  path: CanonicalRoutePath
  /** Output directory under dist/apps/client. '.' for homepage. */
  outDir: string
}

/**
 * Single source of truth for the prerendered public-page set.
 * Imported directly by apps/client/scripts/prerender.ts and
 * apps/client/scripts/generate-seo-files.ts (run via tsx).
 */
export const PRERENDERED_PAGES = [
  { path: '/',                                              outDir: '.' },
  { path: '/about',                                         outDir: 'about' },
  { path: '/faq',                                           outDir: 'faq' },
  { path: '/take-home-assignments',                         outDir: 'take-home-assignments' },
  { path: '/xp-lab-feedback',                               outDir: 'xp-lab-feedback' },
  { path: '/using-ai-without-outsourcing-judgment',         outDir: 'using-ai-without-outsourcing-judgment' },
  { path: '/analytical-workflow',                           outDir: 'analytical-workflow' },
  { path: '/privacy-policy',                                outDir: 'privacy-policy' },
  { path: '/terms-of-service',                              outDir: 'terms-of-service' },
] as const satisfies readonly PrerenderedPage[]

export const PRERENDERED_PATHS: readonly string[] = PRERENDERED_PAGES.map((p) => p.path)

const PREFIXED_LANGS = ['he', 'de'] as const
type PrefixedLang = (typeof PREFIXED_LANGS)[number]

function isPrefixedLang(lang: string): lang is PrefixedLang {
  return (PREFIXED_LANGS as readonly string[]).includes(lang)
}

export function parseLangPrefix(pathname: string): { lang: PrefixedLang; rest: string } | null {
  const match = pathname.match(/^\/(he|de)(\/.*)?$/)
  if (!match) return null
  const lang = match[1] as PrefixedLang
  const rest = match[2] ?? '/'
  return { lang, rest }
}

export function isPrerenderedPath(pathname: string): boolean {
  const prefix = parseLangPrefix(pathname)
  const canonical = prefix ? prefix.rest : pathname
  return PRERENDERED_PATHS.includes(canonical)
}

export function swapLangInPath(pathname: string, target: SupportedLanguage): string {
  const prefix = parseLangPrefix(pathname)
  const canonical = prefix ? prefix.rest : pathname
  if (!PRERENDERED_PATHS.includes(canonical)) return pathname

  if (target === 'en') {
    return canonical
  }
  // Avoid '/he/' for the homepage — emit '/he', not '/he/'.
  return canonical === '/' ? `/${target}` : `/${target}${canonical}`
}

export function localizedUrlsForPath(canonicalPath: string): Record<SupportedLanguage, string> {
  const en = `${SITE_URL}${canonicalPath}`
  const he = canonicalPath === '/' ? `${SITE_URL}/he` : `${SITE_URL}/he${canonicalPath}`
  const de = canonicalPath === '/' ? `${SITE_URL}/de` : `${SITE_URL}/de${canonicalPath}`
  return { en, he, de }
}

export { isPrefixedLang }
